import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { createQueryString } from '@/utils/createQueryString';
import { DEFAULT_ITEMS_PER_PAGE } from '@/constants';
import { FetchUrlType, InStockFilterValues, SortFilterType } from '@/types';
import { useSelector } from 'react-redux';
import { selectUid } from '@/store';

type UseFilterUrlProps = {
  initialSort: SortFilterType;
  type: FetchUrlType;
  code?: string;
  appliedInStock: InStockFilterValues;
  query?: string;
};

export const useFilterUrl = ({
  initialSort,
  type,
  code,
  appliedInStock,
}: UseFilterUrlProps) => {
  const [sort, setSort] = useState<SortFilterType>(initialSort);
  const hasUserChangedSortRef = useRef(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const filtersQuery = createQueryString(searchParams);
  const uid = useSelector(selectUid);
  const uidQuery = uid ? `&uid=${uid}` : '';
  const withFiltersQuery = filtersQuery ? `&${filtersQuery}` : '';

  useEffect(() => {
    if (hasUserChangedSortRef.current) return;
    setSort(initialSort);
  }, [initialSort, type, code]);

  const getFetchUrl = () => {
    let baseUrl: string;
    const inStockParam = `&inStock=${appliedInStock}`;

    switch (type) {
      case FetchUrlType.Search:
        baseUrl = `/search/detail?${uidQuery}&count=${DEFAULT_ITEMS_PER_PAGE}&sort=${sort.code}${inStockParam}`;
        break;

      case FetchUrlType.Promotions:
        baseUrl = `/stocks/${code}/view?count=${DEFAULT_ITEMS_PER_PAGE}&sort=${sort.code}${inStockParam}`;
        break;

      case FetchUrlType.Brands:
        baseUrl = `/brand/${code}/view?count=${DEFAULT_ITEMS_PER_PAGE}&sort=${sort.code}${inStockParam}`;
        break;

      case FetchUrlType.Favourites:
        baseUrl = `/favorites/list?${uidQuery}&count=${DEFAULT_ITEMS_PER_PAGE}&sort=${sort.code}${inStockParam}`;
        break;

      default:
        baseUrl = `/catalog/category/products?count=${DEFAULT_ITEMS_PER_PAGE}&sort=${sort.code}${inStockParam}&sectionUrl=${encodeURIComponent(`${pathname}`)}`;
        break;
    }

    return `${baseUrl}${withFiltersQuery}`;
  };

  const handleSortChange = (newSort: SortFilterType) => {
    hasUserChangedSortRef.current = true;
    setSort(newSort);
  };

  return {
    sort,
    fetchUrl: getFetchUrl(),
    handleSortChange,
  };
};
